/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component, PropsWithChildren } from 'react';
import { ActionMenuItemProps } from './ActionMenu';
import ActionMenuItemView from './ActionMenuItem.view';
import ActionMenuDialogView from './ActionMenuDialog.view';

interface Props {
	itemProps: ActionMenuItemProps & {
		icon: JSX.Element;
		text: string;
	};
	dialogProps: {
		danger?: boolean;
		titleText: string;
		saveButtonText: string;
		wide?: boolean;
		ignoreKeyDownKeys?: string[];
		handleOpen?: () => void;
		handleSave: () => void;
		handleIsSaveDisabled?: () => boolean;
	};
}

interface State {
	dialogOpen: boolean;
}

class DialogActionView extends Component<PropsWithChildren<Props>, State> {
	constructor(props: PropsWithChildren<Props>) {
		super(props);

		this.state = { dialogOpen: false };
		this.handleOpen = this.handleOpen.bind(this);
		this.handleClose = this.handleClose.bind(this);
	}

	handleOpen(): void {
		this.setState({ dialogOpen: true });

		const { handleOpen } = this.props.dialogProps;

		if (handleOpen) {
			handleOpen();
		}
	}

	handleClose(): void {
		this.setState({ dialogOpen: false });
	}

	renderItem(): JSX.Element {
		const { renderMenuItem, icon, text } = this.props.itemProps;

		return <ActionMenuItemView renderMenuItem={renderMenuItem} icon={icon} text={text} action={this.handleOpen} />;
	}

	renderDialog(): JSX.Element | null {
		const { children } = this.props;
		const { danger, titleText, saveButtonText, handleSave, handleIsSaveDisabled, wide, ignoreKeyDownKeys } =
			this.props.dialogProps;
		const { dialogOpen } = this.state;

		if (!dialogOpen) {
			return null;
		}

		return (
			<ActionMenuDialogView
				danger={danger}
				wide={wide}
				titleText={titleText}
				saveButtonText={saveButtonText}
				handleSave={handleSave}
				handleIsSaveDisabled={handleIsSaveDisabled}
				handleClose={this.handleClose}
				ignoreKeyDownKeys={ignoreKeyDownKeys}
			>
				{children}
			</ActionMenuDialogView>
		);
	}

	render(): JSX.Element {
		return (
			<>
				{this.renderItem()}
				{this.renderDialog()}
			</>
		);
	}
}

export default DialogActionView;
